import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, NodeListOf<Element> | null>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  #isRenderingNext = false;
  #isRenderingPrevious = false;

  // Watermarks track the highest/lowest page we've attempted to render.
  // Needed to skip empty pages (Shopify pagination holes where a page
  // returns 0 products). Without these, #getPage() would keep returning
  // the same empty page number because the last card's data-page never advances.
  #highestAttemptedPage = 0;
  #lowestAttemptedPage = Infinity;

  /** @type {Set<string>} */
  #seenStyles = new Set();

  /** @type {Set<number>} */
  #fetchingPages = new Set();

  /** @type {Map<number, Promise<void>>} */
  #fetchPromises = new Map();

  static PREFETCH_DEPTH = 3;

  // Static timestamps survive component reconnection during morph.
  // #observerBlockedUntil: blocks IntersectionObserver auto-renders
  // #reducedDepthUntil: limits prefetch depth in renderNextPage
  static #observerBlockedUntil = 0;
  static #reducedDepthUntil = 0;

  // Set to true on pageswap (user navigated away) to prevent new prefetches
  // from competing for bandwidth with the incoming PDP page load.
  static #isNavigating = false;
  static #pageswapListenerAdded = false;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {IntersectionObserver | undefined} */
  #pageVisibilityObserver;

  /** @type {number} */
  #currentUrlPage = 0;

  connectedCallback() {
    super.connectedCallback();

    // Register once: set #isNavigating when the user leaves the page so
    // in-flight prefetch slots are not wasted competing with PDP bandwidth.
    if (!PaginatedList.#pageswapListenerAdded) {
      PaginatedList.#pageswapListenerAdded = true;
      window.addEventListener('pageswap', () => {
        PaginatedList.#isNavigating = true;
      });
    }

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    // If reconnecting after a filter/sort morph, skip aggressive prefetching.
    // Instead, set up scroll-gated infinite scroll: the IntersectionObserver
    // guard blocks auto-renders, and a scroll handler clears the flag when
    // the user scrolls. Without this, the morph reconnection triggers
    // connectedCallback → prefetchAhead(3) → cascade of 6+ fetches,
    // 10+ long tasks, and CLS > 0.1.
    const isPostFilter = performance.now() < PaginatedList.#observerBlockedUntil;
    if (!isPostFilter) {
      this.#prefetchAhead(PaginatedList.PREFETCH_DEPTH);
      this.#prefetchBehind(PaginatedList.PREFETCH_DEPTH);
      this.#observeViewMore();
    } else {
      // Observer fires immediately but the callback guard blocks renders.
      this.#observeViewMore();
      // After 2s, clear the observer guard and re-trigger observation.
      // 2s gives the user time to see filtered results before more pages
      // start loading. The depth limit (#reducedDepthUntil) stays active
      // longer to prevent aggressive prefetch cascades.
      setTimeout(() => {
        if (!this.isConnected) return;
        PaginatedList.#observerBlockedUntil = 0;
        this.#reobserveViewMore('next');
      }, 2000);
      // Prefetch 1 page ahead so it's ready when auto-load kicks in.
      // Use setTimeout instead of requestIdleCallback — after morph the
      // browser stays busy and requestIdleCallback may never fire.
      setTimeout(() => {
        if (!this.isConnected) return;
        this.#prefetchAhead(1);
      }, 100);
    }

    // Deduplicate <style> blocks on the initial page AND populate the
    // seen-styles set for infinite scroll pages. Each product card can
    // generate ~300 lines of identical CSS (from text.liquid etc.).
    // With 24 cards that's ~7200 lines of duplicate CSS in the DOM.
    for (const style of this.querySelectorAll('style')) {
      const text = style.textContent.trim();
      if (!text) { style.remove(); continue; }
      if (this.#seenStyles.has(text)) {
        style.remove();
      } else {
        this.#seenStyles.add(text);
      }
    }

    // Optimize initial page images: only first N cards get eager loading,
    // all other cards and non-first-slide images get lazy loading.
    this.#optimizeInitialPageImages();

    // Track the initial page number from the URL
    const url = new URL(window.location.href);
    this.#currentUrlPage = Number(url.searchParams.get('page')) || 1;

    // Initialize the page visibility tracker to update URL based on
    // what the user actually sees, not what was just loaded
    this.#initPageVisibilityTracker();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    if (this.#pageVisibilityObserver) {
      this.#pageVisibilityObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              // During post-filter recovery the grid has only 1 page,
              // putting viewMoreNext inside the 1600px rootMargin.
              // Without this guard the observer fires immediately,
              // triggering a cascade of renders, long tasks, and CLS.
              if (performance.now() < PaginatedList.#observerBlockedUntil) return;

              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '1600px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    if (PaginatedList.#isNavigating) return;
    if (this.pages.has(pageNumber)) return;

    // If a fetch is already in-flight for this page, return the same promise so
    // callers waiting on #fetchPage properly await the real result instead of
    // resolving early and reading pages.get(pageNumber) while it is still null.
    // Without this, #prefetchAhead and the IntersectionObserver-triggered
    // #fetchPage race: the observer fires before the prefetch finishes, calls
    // #fetchPage → #fetchSpecificPage returns immediately (already fetching) →
    // resolvePromise fires → #renderNextPage reads pages.get(n) = null → early
    // return → watermark advances → stuck past lastPage forever.
    if (this.#fetchingPages.has(pageNumber)) {
      return this.#fetchPromises.get(pageNumber);
    }

    this.#fetchingPages.add(pageNumber);

    const promise = (async () => {
      try {
        const pageInfo = { page: pageNumber, url };

        if (!url) {
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.set('page', pageNumber.toString());
          newUrl.hash = '';
          pageInfo.url = newUrl;
        }

        if (!this.#shouldUsePage(pageInfo)) return;
        const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);

        // Yield before the expensive DOMParser + optimizeCards work so the
        // browser can process user input and paint between page parses.
        // Without this, parallel fetches completing close together chain
        // their synchronous parse calls into a single ~900ms long task.
        await new Promise((r) => setTimeout(r, 0));

        // Pre-parse and optimize cards during the fetch phase (async, in background)
        // so they're ready to append instantly when the user scrolls to the trigger.
        // Previously, parsing happened synchronously during render, blocking the main thread.
        const cards = this.#parsePageContent(pageContent);
        this.pages.set(pageNumber, cards);
      } finally {
        this.#fetchingPages.delete(pageNumber);
        this.#fetchPromises.delete(pageNumber);
      }
    })();

    this.#fetchPromises.set(pageNumber, promise);
    return promise;
  }

  /**
   * Prefetches multiple pages ahead of the current scroll position.
   * Each page response is ~1.5 MB and takes ~2-3s on average connections,
   * so prefetching 3 pages in parallel gives the user a large buffer
   * before hitting un-cached pages during fast scrolling.
   * Calls #fetchSpecificPage which deduplicates, so already-fetched
   * or in-flight pages are skipped.
   * @param {number} depth - How many pages ahead to prefetch
   */
  #prefetchAhead(depth) {
    const { cards, grid } = this.refs;
    if (!Array.isArray(cards) || !grid) return;

    const lastCard = cards[cards.length - 1];
    if (!lastCard) return;

    const currentMaxPage = Math.max(
      Number(lastCard.dataset.page) || 0,
      this.#highestAttemptedPage
    );
    const lastPage = Number(grid.dataset.lastPage) || 0;

    for (let i = 1; i <= depth; i++) {
      const targetPage = currentMaxPage + i;
      if (targetPage > 0 && targetPage <= lastPage) {
        this.#fetchSpecificPage(targetPage);
      }
    }
  }

  /**
   * Prefetches multiple pages behind the current scroll position.
   * Symmetric to #prefetchAhead — ensures a buffer of cached pages
   * above the viewport so backward scrolling feels instant.
   * @param {number} depth - How many pages behind to prefetch
   */
  #prefetchBehind(depth) {
    const { cards, grid } = this.refs;
    if (!Array.isArray(cards) || !grid) return;

    const firstCard = cards[0];
    if (!firstCard) return;

    const currentMinPage = Math.min(
      Number(firstCard.dataset.page) || Infinity,
      this.#lowestAttemptedPage
    );

    for (let i = 1; i <= depth; i++) {
      const targetPage = currentMinPage - i;
      if (targetPage > 0) {
        this.#fetchSpecificPage(targetPage);
      }
    }
  }

  async #renderNextPage() {
    if (this.#isRenderingNext) return;
    this.#isRenderingNext = true;

    try {
      const { grid } = this.refs;

      if (!grid) return;

      const nextPage = this.#getPage('next');

      if (!nextPage || !this.#shouldUsePage(nextPage)) return;

      // Advance the watermark so #getPage skips past this page if it's empty
      this.#highestAttemptedPage = Math.max(this.#highestAttemptedPage, nextPage.page);

      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        // Page not cached yet — show loading indicator so the user
        // knows content is on the way instead of seeing dead space.
        this.setAttribute('loading-next', '');

        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;

        this.removeAttribute('loading-next');

        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements) return;
      }

      // Empty page (Shopify pagination hole) — skip it and re-trigger
      if (nextPageItemElements.length === 0) {
        this.#reobserveViewMore('next');
        this.#fetchPage('next');
        return;
      }

      grid.append(...nextPageItemElements);

      // After append, promote images that landed at or near the viewport to
      // eager loading so they start downloading immediately instead of waiting
      // for the browser's lazy-load rootMargin check. This prevents the blank
      // gray boxes visible when the user scrolls quickly into a freshly-appended
      // page whose images are still queued as lazy.
      // We use a rAF so layout is settled and getBoundingClientRect is accurate.
      requestAnimationFrame(() => {
        this.#promoteNearViewportImages(nextPageItemElements);
      });

      this.#aspectRatioHelper.processNewElements();

      // Track the first card of the new page for visibility-based URL updates
      this.#observePageBoundaryCard(nextPageItemElements, nextPage.page);

      // Use reduced prefetch depth during recovery to avoid cascading
      // 6+ fetches that create long task storms.
      const isRecovery = performance.now() < PaginatedList.#reducedDepthUntil;
      this.#prefetchAhead(isRecovery ? 1 : PaginatedList.PREFETCH_DEPTH);
    } finally {
      this.#isRenderingNext = false;
      // Re-observe viewMoreNext so the IntersectionObserver fires again.
      // After append, the span may stay inside the rootMargin zone without
      // an in→out→in transition, causing the observer to never re-trigger.
      // Must be AFTER clearing the guard so the callback can proceed.
      if (performance.now() < PaginatedList.#reducedDepthUntil) {
        // During recovery, delay re-observe by 2s to prevent a tight
        // render loop. Each page render triggers reobserve → observer
        // fires immediately → next render, creating cascading long tasks.
        setTimeout(() => {
          if (this.isConnected) this.#reobserveViewMore('next');
        }, 2000);
      } else {
        this.#reobserveViewMore('next');
      }
    }
  }

  async #renderPreviousPage() {
    if (this.#isRenderingPrevious) return;
    this.#isRenderingPrevious = true;

    try {
      const { grid } = this.refs;

      if (!grid) return;

      const previousPage = this.#getPage('previous');
      if (!previousPage || !this.#shouldUsePage(previousPage)) return;

      // Advance the watermark so #getPage skips past this page if it's empty
      this.#lowestAttemptedPage = Math.min(this.#lowestAttemptedPage, previousPage.page);

      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        // Page not cached yet — show loading indicator
        this.setAttribute('loading-previous', '');

        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('previous');

        await promise;

        this.removeAttribute('loading-previous');

        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements) return;
      }

      // Empty page (Shopify pagination hole) — skip it and re-trigger
      if (previousPageItemElements.length === 0) {
        this.#reobserveViewMore('previous');
        this.#fetchPage('previous');
        return;
      }

      // Force first N prepended card images to eager loading.
      // When prepending, these cards land right at the current viewport —
      // lazy loading won't trigger fast enough, causing visible gray boxes.
      const EAGER_PREPEND_LIMIT = 10;
      let cardIdx = 0;
      for (const card of previousPageItemElements) {
        if (cardIdx >= EAGER_PREPEND_LIMIT) break;
        const firstImg = card.querySelector('img');
        if (firstImg) {
          firstImg.setAttribute('loading', 'eager');
          firstImg.removeAttribute('fetchpriority');
          firstImg.removeAttribute('decoding');
        }
        cardIdx++;
      }

      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      // Prepend the new elements
      grid.prepend(...previousPageItemElements);

      this.#aspectRatioHelper.processNewElements();

      // Track the first card of the new page for visibility-based URL updates
      this.#observePageBoundaryCard(previousPageItemElements, previousPage.page);

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      // Prefetch multiple previous pages — symmetric to #prefetchAhead
      this.#prefetchBehind(PaginatedList.PREFETCH_DEPTH);
    } finally {
      this.#isRenderingPrevious = false;
      // Re-observe viewMorePrevious so the observer fires again.
      // Must be AFTER clearing the guard so the callback can proceed.
      this.#reobserveViewMore('previous');
    }
  }

  /**
   * Unobserve + re-observe a viewMore sentinel so the IntersectionObserver
   * fires again. After grid.append/prepend the sentinel can stay inside the
   * rootMargin zone without an in→out→in transition, causing the observer
   * to never re-trigger during fast scrolling.
   * @param {'next' | 'previous'} direction
   */
  #reobserveViewMore(direction) {
    const ref = direction === 'next' ? 'viewMoreNext' : 'viewMorePrevious';
    const el = this.refs[ref];
    if (!el || !this.infinityScrollObserver) return;
    this.infinityScrollObserver.unobserve(el);
    this.infinityScrollObserver.observe(el);
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);

    // Use watermarks to skip past empty pages (Shopify pagination holes)
    const effectivePage = isPrevious
      ? Math.min(currentCardPage, this.#lowestAttemptedPage)
      : Math.max(currentCardPage, this.#highestAttemptedPage);
    const page = isPrevious ? effectivePage - 1 : effectivePage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | null | undefined}
   */
  #getGridForPage(page) {
    return this.pages.get(page);
  }

  /**
   * Parses raw section HTML and returns optimized card elements.
   * @param {string} html - Raw section HTML from the section renderer
   * @returns {NodeListOf<Element> | null}
   */
  #parsePageContent(html) {
    const parsedPage = new DOMParser().parseFromString(html, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return null;

    const cards = gridElement.querySelectorAll(':scope > [ref="cards[]"]');
    this.#optimizeCards(cards);
    return cards;
  }

  /**
   * Optimizes cards before they are appended to the live DOM:
   * - Forces lazy loading on all images (infinite scroll pages should
   *   not eagerly load 16+ images that may be off-screen).
   * - Removes duplicate <style> blocks that the Horizon block system
   *   renders identically inside every card (~400 lines each).
   * - Strips empty <style> tags.
   * @param {NodeListOf<Element>} cards
   */
  #optimizeCards(cards) {
    const MAX_SRCSET_WIDTH = 1200;
    const MAX_SLIDES = 2;

    for (const card of cards) {
      // NOTE: We intentionally do NOT add content-visibility: auto here.
      // While it reduces rendering cost for off-screen cards, it causes
      // images to flash/re-decode when scrolling back up — the user sees
      // gray placeholder boxes instead of instant images. The production
      // store (padelproshop.com) doesn't use it and feels faster.
      // DOM size is already controlled by limiting slides to 2 per card
      // and deduplicating <style> blocks.

      // 1. Force lazy loading & trim srcset — the initial page already
      //    has eager images; subsequent pages should not compete.
      //    Cards are ~350 px wide (700 px @2x). Entries above 1200 w
      //    are never selected by the browser but bloat HTML.
      //    Note: decoding is intentionally NOT set to "async" here —
      //    leaving it at the default ("auto") lets the browser decide.
      //    Setting decoding="async" allows the browser to paint a frame
      //    without the image even when it's already downloaded, producing
      //    a visible gray flash. "auto" avoids that without blocking the
      //    main thread (the browser uses async decode for lazy images anyway).
      for (const img of card.querySelectorAll('img')) {
        img.setAttribute('loading', 'lazy');
        img.removeAttribute('decoding');
        img.removeAttribute('fetchpriority');

        const srcset = img.getAttribute('srcset');
        if (srcset) {
          const trimmed = srcset.split(',').filter(entry => {
            const m = entry.trim().match(/(\d+)w\s*$/);
            return !m || Number(m[1]) <= MAX_SRCSET_WIDTH;
          }).join(',');
          if (trimmed !== srcset) img.setAttribute('srcset', trimmed);
        }
      }

      // 2. Limit slideshow slides to the first 2 (main image + hover
      //    preview). Products can have 11 images — keeping all of them
      //    in the grid adds ~9 extra <img> elements per card × 500 cards
      //    = ~4,500 unnecessary DOM nodes + potential lazy-load triggers.
      for (const slideshow of card.querySelectorAll('slideshow-component')) {
        const slides = slideshow.querySelectorAll('slideshow-slide');
        if (slides.length > MAX_SLIDES) {
          for (let i = MAX_SLIDES; i < slides.length; i++) {
            slides[i]?.remove();
          }
          // Update component metadata so arrows/dots know the new count
          slideshow.setAttribute('data-generic-media-size', String(MAX_SLIDES));
          // Trim the CSS timeline custom property to match
          const timelineNames = Array.from({ length: MAX_SLIDES }, (_, k) => `--slide-${k + 1}`).join(', ');
          /** @type {HTMLElement} */ (slideshow).style.setProperty('--slideshow-timeline', timelineNames);
        }
      }

      // 3. Deduplicate <style> blocks to reduce DOM bloat.
      //    Scoped styles (with unique class names) have different content
      //    and are kept; generic styles identical across cards are stripped.
      for (const style of card.querySelectorAll('style')) {
        const text = style.textContent.trim();
        if (!text) { style.remove(); continue; }
        if (this.#seenStyles.has(text)) {
          style.remove();
        } else {
          this.#seenStyles.add(text);
        }
      }
    }
  }

  /**
   * After a page is appended, promotes the first image of each card that
   * landed at or near the current viewport from lazy to eager loading.
   * This prevents the blank gray boxes that appear when the user scrolls
   * into a freshly-appended page before the browser's lazy-load queue
   * has had time to start those downloads.
   *
   * The threshold is viewport height + 800px (roughly 1-2 card rows below
   * the fold) — enough to cover the visible area plus a small buffer for
   * fast scrolling, without eagerly loading cards far off-screen.
   *
   * @param {NodeListOf<Element>} cards
   */
  #promoteNearViewportImages(cards) {
    const threshold = window.innerHeight + 800;
    for (const card of cards) {
      const rect = card.getBoundingClientRect();
      // Card is at or below the current viewport but within the threshold
      if (rect.top <= threshold) {
        const firstImg = card.querySelector('img');
        if (firstImg && firstImg.getAttribute('loading') === 'lazy' && !firstImg.complete) {
          firstImg.setAttribute('loading', 'eager');
          firstImg.removeAttribute('decoding');
        }
      }
    }
  }

  /**
   * Optimizes images on the initial page load:
   * - First N cards' first image: forced to eager (covers edge cases where
   *   server-side section.index check rendered them as lazy).
   * - All other cards and non-first-slide images: forced to lazy loading.
   * This must run before the user scrolls so below-fold images respect
   * the dynamically-set loading="lazy" attribute.
   */
  #optimizeInitialPageImages() {
    const EAGER_CARD_LIMIT = 10;
    const { cards } = this.refs;
    if (!cards?.length) return;

    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      if (!card) continue;
      const images = card.querySelectorAll('img');
      for (let j = 0; j < images.length; j++) {
        const img = /** @type {HTMLImageElement} */ (images[j]);
        if (i < EAGER_CARD_LIMIT && j === 0) {
          // First slide of first N cards: ensure eager loading without
          // decoding=async so the image renders immediately when decoded.
          img.setAttribute('loading', 'eager');
          img.removeAttribute('decoding');
          // LCP candidate: first card gets high priority; rest get auto
          if (i === 0) {
            img.setAttribute('fetchpriority', 'high');
          } else {
            img.removeAttribute('fetchpriority');
          }
        } else {
          // Everything else gets lazy loading. We do NOT set decoding=async
          // because it allows the browser to paint a frame without the image
          // even after it is downloaded, producing a gray flash on scroll.
          img.setAttribute('loading', 'lazy');
          img.removeAttribute('decoding');
          img.removeAttribute('fetchpriority');
        }
      }
    }

    // After setting all loading attrs, immediately unlock images that are
    // already within 2500px of the viewport. content-visibility:auto on card
    // ancestors blocks the browser's native lazy-load threshold (~1200px),
    // so without this cards 3-8 in the grid never preload until they scroll
    // into view, causing visible blank boxes. 2500px ≈ 5 card rows at 1920px.
    const viewportH = window.innerHeight;
    for (let i = EAGER_CARD_LIMIT; i < cards.length; i++) {
      const card = cards[i];
      if (!card) continue;
      const rect = card.getBoundingClientRect();
      if (rect.top < viewportH + 2500) {
        for (const img of card.querySelectorAll('img[loading="lazy"]')) {
          img.removeAttribute('loading');
        }
      }
    }
  }

  /**
   * Initializes the page visibility tracker.
   * Uses IntersectionObserver to detect when the first card of each page
   * enters the top portion of the viewport, then updates the URL with
   * replaceState. This decouples URL updates from page loading — the URL
   * only changes when the user actually SEES a page's content.
   */
  #initPageVisibilityTracker() {
    this.#pageVisibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const page = Number(/** @type {HTMLElement} */ (entry.target).dataset.pageBoundary);
            if (page && page !== this.#currentUrlPage) {
              this.#currentUrlPage = page;
              const url = new URL(window.location.href);
              if (page <= 1) {
                url.searchParams.delete('page');
              } else {
                url.searchParams.set('page', String(page));
              }
              url.hash = '';
              history.replaceState('', '', url.toString());
            }
          }
        }
      },
      {
        // Observe only the top 30% of the viewport — the URL updates when
        // a page boundary card scrolls into view near the top
        rootMargin: '0px 0px -70% 0px',
      }
    );

    // Observe the first card of the initial page
    this.#observeInitialPageBoundaries();
  }

  /**
   * Observes the first card of each page already in the DOM (initial load).
   */
  #observeInitialPageBoundaries() {
    const { cards } = this.refs;
    if (!cards?.length) return;

    const seenPages = new Set();
    for (const card of cards) {
      const page = Number(card.dataset.page);
      if (page && !seenPages.has(page)) {
        seenPages.add(page);
        card.dataset.pageBoundary = String(page);
        this.#pageVisibilityObserver?.observe(card);
      }
    }
  }

  /**
   * Marks and observes the first card of a newly loaded page.
   * @param {NodeListOf<Element>} cards - The cards from the new page
   * @param {number} page - The page number
   */
  #observePageBoundaryCard(cards, page) {
    if (!cards?.length || !this.#pageVisibilityObserver) return;
    const firstCard = cards[0];
    if (firstCard instanceof HTMLElement) {
      firstCard.dataset.pageBoundary = String(page);
      this.#pageVisibilityObserver.observe(firstCard);
    }
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    const now = performance.now();
    PaginatedList.#observerBlockedUntil = now + 5000;
    PaginatedList.#reducedDepthUntil = now + 10000;
    this.pages.clear();
    this.#fetchingPages.clear();
    this.#fetchPromises.clear();
    this.#seenStyles.clear();
    this.#highestAttemptedPage = 0;
    this.#lowestAttemptedPage = Infinity;

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Disconnect the infinite scroll observer now — we'll reconnect
    // it on the next user scroll to prevent the IntersectionObserver
    // from firing immediately after morph (which causes a cascade of
    // page renders, long tasks, and CLS > 0.1).
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }

    // Trim infinite-scroll pages from the grid so morph only diffs page 1
    // against the new filtered page 1, instead of comparing hundreds of
    // stale product cards that will all be removed anyway (O(n²) → O(n)).
    const { grid } = this.refs;
    if (grid) {
      const firstPageItems = grid.querySelectorAll('[data-page="1"]');
      const firstPageNum = firstPageItems.length > 0 ? '1' : null;

      if (firstPageNum) {
        for (let i = grid.children.length - 1; i >= 0; i--) {
          const child = grid.children[i];
          if (child instanceof HTMLElement && child.dataset.page && child.dataset.page !== firstPageNum) {
            child.remove();
          }
        }
      }
    }

    // Wait for morph() to finish updating the DOM with the new filtered/sorted
    // content before reinitializing the infinite scroll observers.
    // We watch for ANY mutation (childList or attribute) because:
    // - Filter changes may alter data-last-page (different product count)
    // - Sort changes keep the same data-last-page but replace all child cards
    // The observer is only created here (inside #handleFilterUpdate), so the
    // first mutation is guaranteed to be from the morph after a filter/sort.
    if (grid) {
      const observer = new MutationObserver(() => {
        observer.disconnect();

        if (!this.isConnected) return;

        // Force refs update — the morph replaces card elements but the
        // Component base class MutationObserver may not have fired yet,
        // leaving this.refs.cards pointing at stale disconnected nodes.
        // Without this, #prefetchAhead and #renderNextPage silently fail
        // because they rely on this.refs.cards to determine page numbers.
        this.updatedCallback();

        // Re-apply image loading optimizations on the new filtered cards.
        // setTimeout(0) ensures the morph and its queueMicrotask(updatedCallback)
        // calls have all settled before we read this.refs.cards and modify
        // image attributes. rAF is too early — morph dispatches updatedCallback
        // via queueMicrotask which runs before the next animation frame.
        setTimeout(() => {
          if (!this.isConnected) return;
          this.updatedCallback();
          this.#optimizeInitialPageImages();
        }, 0);

        // Re-observe (guard blocks auto-renders for now).
        this.#observeViewMore();

        // After 2s, clear the observer guard so infinite scroll resumes.
        // The depth limit (#reducedDepthUntil) stays active longer.
        setTimeout(() => {
          if (!this.isConnected) return;
          PaginatedList.#observerBlockedUntil = 0;
          this.#reobserveViewMore('next');
        }, 2000);

        // Prefetch 1 page ahead so it's ready when auto-load kicks in.
        // Use setTimeout instead of requestIdleCallback — after morph the
        // browser stays busy with paint/layout and requestIdleCallback may
        // never fire, leaving infinite scroll without prefetched pages.
        setTimeout(() => {
          if (!this.isConnected) return;
          this.#prefetchAhead(1);
        }, 100);
      });

      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
      });

      // Fallback in case the mutation never fires (e.g., identical content)
      setTimeout(() => observer.disconnect(), 3000);
    }
  };
}
